import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	createCell4GFdd,
	createCell4GTdd,
	createCell5G,
	get4GFddBySite,
	get4GTddBySite,
	get5GBySite,
	removeCell4GFdd,
	removeCell4GTdd,
	removeCell5G,
	removeCells4GFdd,
	removeCells4GTdd,
	removeCells5G,
	updateCell4GFdd,
	updateCell4GTdd,
	updateCell5G,
} from '@api/manageCell';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { TTechnologyNameTitle } from '@interfaces/common/common';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { ESnackbarTypes, ETechnologiesValues } from '@enums/common';

import {
	ANTENNA_MODELS,
	CELLS_BY_SITE,
	CELLS_FOR_SITE_SECTORS,
	CREATE_CELL_4G_FDD,
	CREATE_CELL_4G_TDD,
	CREATE_CELL_5G,
	REMOVE_CELL,
	REMOVE_CELLS,
	UPDATE_CELL_4G_FDD,
	UPDATE_CELL_4G_TDD,
	UPDATE_CELL_5G,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseManageCellBySitesParams {
	closeCellModal: () => void;
	id: string;
	technology?: TTechnologyNameTitle & { node: string };
	closeDeleteConfirm: () => void;
}

export const useManageCellsBySites = (params: IUseManageCellBySitesParams) => {
	const dispatch = useAppDispatch();

	const {
		closeCellModal,
		id,
		technology,
		closeDeleteConfirm,
	} = params || {};
	const queryClient = useQueryClient();

	const {
		mutate: createCell4GFddHandler,
		isLoading: isLoadingCreateCell4GFdd,
	} = useMutation({
		mutationKey: [CREATE_CELL_4G_FDD],
		mutationFn: createCell4GFdd,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createdCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return [
				queryClient.invalidateQueries([CELLS_BY_SITE]),
				queryClient.invalidateQueries([ANTENNA_MODELS]),
			];
		},
	});

	const {
		mutate: updateCell4GFddHandler,
		isLoading: isLoadingUpdateCell4GFdd,
	} = useMutation({
		mutationKey: [UPDATE_CELL_4G_FDD],
		mutationFn: updateCell4GFdd,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return [
				queryClient.invalidateQueries([CELLS_BY_SITE]),
				queryClient.invalidateQueries([ANTENNA_MODELS]),
			];
		},
	});

	const {
		mutate: createCell4GTddHandler,
		isLoading: isLoadingCreateCell4GTdd,
	} = useMutation({
		mutationKey: [CREATE_CELL_4G_TDD],
		mutationFn: createCell4GTdd,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createdCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return [
				queryClient.invalidateQueries([CELLS_BY_SITE]),
				queryClient.invalidateQueries([ANTENNA_MODELS]),
			];
		},
	});

	const {
		mutate: updateCell4GTddHandler,
		isLoading: isLoadingUpdateCell4GTdd,
	} = useMutation({
		mutationKey: [UPDATE_CELL_4G_TDD],
		mutationFn: updateCell4GTdd,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return [
				queryClient.invalidateQueries([CELLS_BY_SITE]),
				queryClient.invalidateQueries([ANTENNA_MODELS]),
			];
		},
	});

	const {
		mutate: createCell5GHandler,
		isLoading: isLoadingCreateCell5G,
	} = useMutation({
		mutationKey: [CREATE_CELL_5G],
		mutationFn: createCell5G,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createdCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return [
				queryClient.invalidateQueries([CELLS_BY_SITE]),
				queryClient.invalidateQueries([ANTENNA_MODELS]),
			];
		},
	});

	const {
		mutate: updateCell5GHandler,
		isLoading: isLoadingUpdateCell5G,
	} = useMutation({
		mutationKey: [UPDATE_CELL_5G],
		mutationFn: updateCell5G,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updatedCell,
				type: ESnackbarTypes.success,
			}));

			closeCellModal();

			return [
				queryClient.invalidateQueries([CELLS_BY_SITE]),
				queryClient.invalidateQueries([ANTENNA_MODELS]),
				queryClient.invalidateQueries([CELLS_FOR_SITE_SECTORS]),
			];
		},
	});

	const {
		data: dataSites,
		isLoading: isLoadingSites,
		isFetching: isFetchingSites,
	} = useQuery({
		queryKey: [
			CELLS_BY_SITE,
			id,
			technology?.name,
		],
		queryFn: () => {
			switch (technology?.name) {
				case ETechnologiesValues.T_4G_TDD:
					return get4GTddBySite(id);
				case ETechnologiesValues.T_4G_FDD:
					return get4GFddBySite(id);
				case ETechnologiesValues.T_5G:
					return get5GBySite(id);
				default:
					return Promise.resolve({ items: [], count: 0 });
			}
		},
		refetchOnWindowFocus: false,
	});

	const {
		mutate: handleDeleteCell,
		isLoading: isLoadingDeleteCell,
	} = useMutation({
		mutationKey: [REMOVE_CELL],
		mutationFn: ({ id, cellId }: { id: string; cellId: number }) => {
			switch (technology?.name) {
				case ETechnologiesValues.T_4G_TDD:
					return removeCell4GTdd({ id, cellId });
				case ETechnologiesValues.T_4G_FDD:
					return removeCell4GFdd({ id, cellId });
				case ETechnologiesValues.T_5G:
					return removeCell5G({ id, cellId });
				default:
					return;
			}
		},
		onSuccess: () => {
			closeDeleteConfirm();

			dispatch(setSnackbar({
				message: responseMessages.removedCell,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([CELLS_BY_SITE]);
		},
		onError: (err: IResponseError) => {
			closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeleteCells,
		isLoading: isLoadingDeleteCells,
	} = useMutation({
		mutationKey: [REMOVE_CELLS],
		mutationFn: (cellIds: Array<number>) => {
			switch (technology?.name) {
				case ETechnologiesValues.T_4G_TDD:
					return removeCells4GTdd({ id, cellIds });
				case ETechnologiesValues.T_4G_FDD:
					return removeCells4GFdd({ id, cellIds });
				case ETechnologiesValues.T_5G:
					return removeCells5G({ id, cellIds });
				default:
					return;
			}
		},
		onSuccess: () => {
			closeDeleteConfirm();

			dispatch(setSnackbar({
				message: responseMessages.removedCells,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([CELLS_BY_SITE]);
		},
		onError: (err: IResponseError) => {
			closeDeleteConfirm();

			handleErrorFromMessage(err);
		},
	});

	return {
		isLoadingForm: isLoadingCreateCell4GTdd || isLoadingCreateCell4GFdd || isLoadingCreateCell5G
			|| isLoadingUpdateCell5G || isLoadingUpdateCell4GFdd || isLoadingUpdateCell4GTdd,
		createCell4GTddHandler,
		createCell4GFddHandler,
		createCell5GHandler,
		isLoadingCellTable: isLoadingSites || isFetchingSites || isLoadingDeleteCell || isLoadingDeleteCells,
		cellsBySite: dataSites?.items || [],
		cellsCount: dataSites?.count || 0,
		handleDeleteCell,
		handleDeleteCells,
		updateCell5GHandler,
		updateCell4GFddHandler,
		updateCell4GTddHandler,
	};
};
